import classNames from "classnames";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { AppFileTag } from "../../shared/components/AppFileTag";
import { SendMessageButton } from "../ChatComponents/MessageSendingInput/Actions/SendMessageButton";
import { DisplayedMessages } from "../ChatComponents/DisplayedMessages";
import { MessageSendingInput } from "../ChatComponents/MessageSendingInput";
import { ChatCompletionsMessageObject } from "../../services/api/instances/completionsApi/types";
import { PaperclipIcon } from "../../shared/icons/PaperclipIcon";
import { AppLoading } from "../../shared/components/AppLoading";
import { AppTooltip } from "../../shared/components/AppTooltip";
import { AppIconButton } from "../../shared/components/AppIconButton";
import { useMenu } from "../hooks/use-menu";
import { AttachedFileMenu } from "./Components/AttachedFileMenu";
import { TEntityInfo } from "../types/utility";
import { useAppSelector } from "../../services/redux/hooks/use-selector";
import { selectUserArtifactGroup } from "../../features/Auth/redux/selectors";
import { UploadFileButton } from "../Components/Buttons/UploadFileButton";
import { TModalsIds } from "../../app/state/modalSlice/types";
import { MessageSendingInputStyle } from "../ChatComponents/MessageSendingInput/types";
import { usePasteFileUpload } from "./hook/use-past-file-upload";
import styles from "./styles.module.css";

interface Props {
  messages: ChatCompletionsMessageObject[];
  userMessage: string;
  isMessageLoading: boolean;
  handleSendMessage: () => void;
  onChangeUserMessage: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDownMessage: (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => void;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpenAttachedFileModal: (
    id: TModalsIds,
    info: TEntityInfo | null,
  ) => void;
  inputStyle?: MessageSendingInputStyle;
  onDeleteFilaTag?: () => void;
  onFileClick?: (url: string, filename: string) => void;
  additionalOption?: React.ReactNode;
  additionalAction?: React.ReactNode;
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  fileName?: string;
  isResizable?: boolean;
  loadingMessage?: string | React.ReactNode;
  inputPlaceholder?: string;
  isFileLoading?: boolean;
  className?: string;
}

export const Chat = ({
  messages,
  userMessage,
  isMessageLoading,
  handleSendMessage,
  onChangeUserMessage,
  handleKeyDownMessage,
  handleFileUpload,
  handleOpenAttachedFileModal,
  onDeleteFilaTag,
  onFileClick,
  fileName,
  inputStyle = "standard",
  inputRef,
  additionalOption,
  inputPlaceholder,
  additionalAction,
  loadingMessage,
  isResizable,
  isFileLoading,
  className,
}: Props) => {
  const artifact = useAppSelector(selectUserArtifactGroup);
  const { t } = useTranslation();
  const { anchorEl, open, handleMenu, handleMenuClose } = useMenu();

  usePasteFileUpload(handleFileUpload);

  const dialogActions = (
    <>
      {additionalAction && additionalAction}
      <AppTooltip
        title={t("common.attaching_file_tooltip")}
        placement={"top-end"}
      >
        <span tabIndex={0}>
          {artifact ? (
            <AppIconButton onClick={handleMenu}>
              <PaperclipIcon />
            </AppIconButton>
          ) : (
            <UploadFileButton
              icon={<PaperclipIcon />}
              type={"icon_button"}
              handleFileUpload={handleFileUpload}
            />
          )}
        </span>
      </AppTooltip>
      <SendMessageButton sendMessage={handleSendMessage} />
    </>
  );

  const dialogExtraContent = (
    <div
      className={classNames(
        styles["file-viewer"],
        !fileName && styles["file-viewer-close"],
      )}
    >
      {fileName && (
        <AppFileTag
          fileName={fileName ?? ""}
          onDelete={onDeleteFilaTag}
          className={styles["file-tag"]}
        />
      )}
      {isFileLoading && (
        <AppLoading settingSize={18} className={styles["file-loading"]} />
      )}
    </div>
  );

  return (
    <div className={classNames(styles["chat-container"], className)}>
      <DisplayedMessages
        messages={messages}
        isLoading={isMessageLoading}
        className={styles["chat"]}
        isResizable={isResizable}
        loadingMessage={loadingMessage}
        onFileClick={onFileClick}
      />
      <MessageSendingInput
        sendValue={userMessage}
        onChange={onChangeUserMessage}
        handleKeyDown={handleKeyDownMessage}
        actions={dialogActions}
        rows={4}
        extraContent={dialogExtraContent}
        inputStyle={inputStyle}
        inputPlaceholder={
          inputPlaceholder
            ? inputPlaceholder
            : t("assistants.message_input_placeholder")
        }
        isDisabled={isFileLoading || isMessageLoading}
      />
      <AttachedFileMenu
        open={open}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        additionalOption={additionalOption}
        handleUploadFile={(e) => {
          handleFileUpload(e);
          handleMenuClose();
        }}
        openAttachedFileModal={() => {
          handleOpenAttachedFileModal("attached_file_modal", null);
          handleMenuClose();
        }}
        closeMenu={() => handleMenuClose()}
      />
    </div>
  );
};
